import { api } from '../api.js';
import { store } from '../store.js';
import { renderHeader, bindHeader } from '../components/header.js';
import { toast } from '../utils/toast.js';

const TIER_META = {
  rookie:       { label: 'Новичок',         hint: 'только начинаешь — топчик ✌️' },
  regular:      { label: 'В ритме',         hint: 'регулярно заходишь, так держать' },
  daily:        { label: 'Каждый день',     hint: 'прямо ежедневно — респект' },
  athlete:      { label: 'Спорт-режим',     hint: 'атлет, карточки этой недели с особой рамкой' },
  native_bound: { label: 'Почти носитель',  hint: 'ты на стадии «почти native», огонь' },
};

const GOAL_TEXT = {
  mastered: (g) => `Закрыть ${g.targetValue} ${pluralize(g.targetValue, 'фразу', 'фразы', 'фраз')} в память`,
  clean_runs: (g) => `Вкатить ${g.targetValue} чистых ${pluralize(g.targetValue, 'прохода', 'прохода', 'проходов')} без ошибок`,
  target_collocation: (g) => {
    const phrase = g.context?.english ? `«${g.context.english}»` : 'одну фразу';
    return `Довести ${phrase} до победы`;
  },
};

function pluralize(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return 'недавно';
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  return `${d} ${pluralize(d, 'день', 'дня', 'дней')} назад`;
}

export async function renderDashboardPage({ root }) {
  const currentLevel = store.user?.currentLevel ?? 'B1';
  const levels = ['A1', 'A2', 'B1', 'B2'];
  const levelSwitcher = `
    <div class="level-switcher">
      <span class="level-switcher-label">Уровень:</span>
      <div class="level-switcher-pills">
        ${levels.map((l) => `<button class="level-pill ${l === currentLevel ? 'active' : ''}" data-level="${l}">${l}</button>`).join('')}
      </div>
    </div>
  `;

  root.innerHTML = renderHeader({ active: 'home' }) + `
    <div class="container">
      ${levelSwitcher}

      <div class="gamify-hero">
        <div id="form-card" class="gamify-form-card"></div>
        <div id="goal-card" class="gamify-goal-card"></div>
      </div>

      <h2 class="section-title">Путь фразы через уровень ${currentLevel}</h2>
      <div id="journey" class="journey-grid"></div>

      <h2 class="section-title">Чем ты крут</h2>
      <div id="scales" class="scales-grid"></div>

      <div class="cta-row">
        <a class="btn" href="/trainer" data-link>▶ Погнали</a>
      </div>

      <h2 class="section-title">Свежие виктори · последние 6</h2>
      <ul id="collection" class="collection-strip"></ul>
    </div>
  `;
  bindHeader();

  root.querySelectorAll('.level-pill').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const level = btn.dataset.level;
      if (level === store.user?.currentLevel) return;
      btn.disabled = true;
      try {
        const { user } = await api.auth.setLevel(level);
        store.user = user;
        toast(`Уровень переключён на ${level}`, { type: 'success' });
        renderDashboardPage({ root });
      } catch {
        toast('Не получилось переключить уровень', { type: 'error' });
        btn.disabled = false;
      }
    });
  });

  const formCard = root.querySelector('#form-card');
  const goalCard = root.querySelector('#goal-card');
  const scalesEl = root.querySelector('#scales');
  const journeyEl = root.querySelector('#journey');
  const collectionEl = root.querySelector('#collection');

  formCard.innerHTML = '<div class="text-muted">Загрузка…</div>';

  try {
    const data = await api.gamification.dashboard();

    const tier = TIER_META[data.form.tier] ?? TIER_META.rookie;
    formCard.innerHTML = `
      <div class="form-ring" style="--form: ${data.form.score}">
        <svg viewBox="0 0 44 44"><circle class="bg" cx="22" cy="22" r="18"/><circle class="fg" cx="22" cy="22" r="18" stroke-dasharray="${(data.form.score / 100) * 113.1} 113.1"/></svg>
        <div class="form-ring-value">${Math.round(data.form.score)}</div>
      </div>
      <div class="form-card-body">
        <div class="form-tier">${tier.label}</div>
        <div class="text-muted">${tier.hint}</div>
        <div class="form-meta">
          ${data.form.freezeAvailableThisWeek ? '🧊 Заморозка недели ещё в кармане — пропуск дня не снесёт форму' : '🧊 Заморозку недели уже схавала пауза — держим режим'}
        </div>
      </div>
    `;

    const goal = data.dailyGoal;
    const goalPct = Math.min(100, Math.round((goal.currentValue / goal.targetValue) * 100));
    const goalText = (GOAL_TEXT[goal.goalType] ?? (() => 'Цель'))(goal);
    goalCard.innerHTML = `
      <div class="goal-header">
        <span class="goal-kicker">Цель на сегодня</span>
        ${goal.completedAt ? '<span class="goal-done">✓ Закрыто</span>' : ''}
      </div>
      <div class="goal-text">${goalText}</div>
      <div class="progress-bar"><div class="fill" style="width: ${goalPct}%"></div></div>
      <div class="goal-progress text-muted">${goal.currentValue} / ${goal.targetValue}</div>
    `;

    const sp = data.stageProgress || { initial: 0, hint: 0, blind: 0, mastered: 0, notStarted: data.progress.total };
    const totalActive = sp.initial + sp.hint + sp.blind + sp.mastered;
    const journeySteps = [
      {
        cls: 'journey-initial',
        badge: 'ШАГ 01',
        title: 'Знакомство',
        desc: 'Набираешь фразу подряд — пальцы запоминают её раньше головы.',
        count: sp.initial,
        metric: 'до 15 повторов',
        segs: 5,
        filledSegs: Math.min(5, Math.ceil((sp.initial / Math.max(1, data.progress.total)) * 10) || (sp.initial > 0 ? 1 : 0)),
      },
      {
        cls: 'journey-hint',
        badge: 'ШАГ 02',
        title: 'Закрепление',
        desc: 'Фраза возвращается вперемешку. Подсказка пока доступна.',
        count: sp.hint,
        metric: '3 правильных подряд',
        segs: 3,
        filledSegs: sp.hint > 0 ? 2 : 0,
      },
      {
        cls: 'journey-blind',
        badge: 'ШАГ 03',
        title: 'Проверка',
        desc: 'Английский спрятан. Вспоминаешь сам. Провалил — откат.',
        count: sp.blind,
        metric: '2 правильных подряд',
        segs: 2,
        filledSegs: sp.blind > 0 ? 1 : 0,
      },
      {
        cls: 'journey-mastered',
        badge: 'ГОТОВО',
        title: 'В памяти',
        desc: 'Чанк в твоей коллекции с лучшим прогоном и осциллограммой.',
        count: sp.mastered,
        metric: `${data.progress.total - totalActive} ещё впереди`,
        segs: 5,
        filledSegs: 5,
      },
    ];
    journeyEl.innerHTML = journeySteps.map((s) => `
      <div class="journey-card ${s.cls}">
        <div class="journey-badge">${s.badge}</div>
        <div class="journey-count">${s.count}</div>
        <h3>${s.title}</h3>
        <p class="text-muted">${s.desc}</p>
        <div class="journey-metric">${s.metric}</div>
        <div class="segment-bar-demo" style="grid-template-columns: repeat(${s.segs}, 1fr);">
          ${Array.from({ length: s.segs }).map((_, i) => `<div class="seg ${i < s.filledSegs ? 'ok' : ''}"></div>`).join('')}
        </div>
      </div>
    `).join('');

    const scales = [];
    scales.push({
      cls: 'scale-vocab',
      value: `${data.progress.vocabulary}<span class="of">/${data.progress.total}</span>`,
      label: 'Фразы',
      sub: `у тебя в голове на уровне ${data.level}`,
    });
    if (data.progress.fluencyWpm > 0) {
      scales.push({
        cls: 'scale-fluency',
        value: `${data.progress.fluencyWpm}<span class="of"> сл/мин</span>`,
        label: 'Скорость',
        sub: 'средний темп печати за 30 дней',
      });
    }
    if (data.progress.retentionPct !== null) {
      scales.push({
        cls: 'scale-retention',
        value: `${Math.round(data.progress.retentionPct * 100)}%`,
        label: 'Память',
        sub: 'помнишь ли фразы через 3+ дня',
      });
    }

    // Expand remaining cards to fill. One card = hero; two cards = two-col; three = three-col.
    const colCount = scales.length;
    scalesEl.className = `scales-grid scales-cols-${colCount}`;
    const comingSoonPills = [];
    if (!(data.progress.fluencyWpm > 0)) comingSoonPills.push({ label: 'Скорость', unlock: 'появится после первых рядов правильных наборов' });
    if (data.progress.retentionPct === null) comingSoonPills.push({ label: 'Память', unlock: 'когда вернёшься к mastered фразам через 3+ дня' });

    scalesEl.innerHTML = scales
      .map((s) => `
        <div class="scale-card ${s.cls}">
          <div class="value">${s.value}</div>
          <div class="label">${s.label}</div>
          <div class="sub">${s.sub}</div>
        </div>
      `)
      .join('') + (comingSoonPills.length ? `
        <div class="scale-soon">
          <div class="scale-soon-title">Скоро откроется</div>
          <ul>${comingSoonPills.map((p) => `<li><strong>${p.label}</strong> — ${p.unlock}</li>`).join('')}</ul>
        </div>
      ` : '');

    const collectionSection = root.querySelector('#collection')?.closest('.container');
    const collectionTitle = [...root.querySelectorAll('.section-title')].find((el) => el.textContent.includes('виктори'));

    if (!data.recentMastered.length) {
      if (collectionTitle) collectionTitle.style.display = 'none';
      collectionEl.style.display = 'none';
    } else {
      if (collectionTitle) collectionTitle.style.display = '';
      collectionEl.style.display = '';
      collectionEl.innerHTML = data.recentMastered
        .map((m) => `
          <li class="collection-mini">
            <div class="ru">${escapeHtml(m.russian)}</div>
            <div class="en">${escapeHtml(m.english)}</div>
            <div class="meta">
              ${m.bestWpm ? `<span>⚡ ${Math.round(m.bestWpm)} сл/мин</span>` : ''}
              <span>${fmtRelativeTime(m.masteredAt)}</span>
            </div>
          </li>
        `)
        .join('');
    }
  } catch (err) {
    formCard.innerHTML = `<div class="text-muted">Ошибка: ${err.message || 'не удалось загрузить'}</div>`;
  }
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
